import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";

const MUTE_KEY = "sdf_ad_muted_v1";

// Two+ video advertisement section — autoplays muted, 2-second gap between videos,
// loops forever, remembers mute preference, manual dot switch immediately.
// Includes touch-responsive draggable/tappable progress bar and play/pause + fullscreen.
export default function AdVideos({ videos = [] }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(() => {
    const raw = localStorage.getItem(MUTE_KEY);
    return raw === null ? true : raw === "1";
  });
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const gapTimerRef = useRef(null);
  const progressRef = useRef(null);
  const list = videos.length ? videos : [];

  const persistMute = (m) => {
    localStorage.setItem(MUTE_KEY, m ? "1" : "0");
    setMuted(m);
    videoRefs.current.forEach((v) => { if (v) v.muted = m; });
  };

  const playVideo = useCallback((i) => {
    const v = videoRefs.current[i];
    if (!v) return;
    v.currentTime = 0;
    v.muted = muted;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [muted]);

  useEffect(() => {
    if (!list.length) return;
    videoRefs.current.forEach((v, i) => { if (v && i !== active) { v.pause(); } });
    setProgress(0);
    setCurrentTime(0);
    // Set duration from the newly-active video (may be 0 until metadata loads).
    const av = videoRefs.current[active];
    setDuration((av && !isNaN(av.duration) ? av.duration : 0));
    playVideo(active);
    setPlaying(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, list.length]);

  const clearGap = () => {
    if (gapTimerRef.current) {
      clearTimeout(gapTimerRef.current);
      gapTimerRef.current = null;
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    clearGap();
    gapTimerRef.current = setTimeout(() => setActive((a) => (a + 1) % list.length), 2000);
  };

  const onDotClick = (i) => { clearGap(); setActive(i); };

  const togglePlay = () => {
    const v = videoRefs.current[active];
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => persistMute(!muted);

  const onTimeUpdate = (e) => {
    if (dragging) return;
    const v = e.target;
    setCurrentTime(v.currentTime);
    if (v.duration) setProgress((v.currentTime / v.duration) * 100);
  };

  const onLoaded = (e) => setDuration(e.target.duration || 0);

  const seekToClientX = (clientX) => {
    const rail = progressRef.current;
    const v = videoRefs.current[active];
    if (!rail || !v) return;
    const rect = rail.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setProgress(pct * 100);
    if (v.duration) {
      const t = pct * v.duration;
      v.currentTime = t;
      setCurrentTime(t);
    }
  };

  // Pointer events cover both mouse + touch for smooth seek/drag.
  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    const v = videoRefs.current[active];
    if (v && !v.paused) v.pause();
    seekToClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    seekToClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    seekToClientX(e.clientX);
    const v = videoRefs.current[active];
    if (v && playing) { v.play().catch(() => {}); }
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // Lock orientation to landscape whenever the video container enters
  // fullscreen, and release the lock on exit. Silently no-ops on browsers /
  // devices that don't support the Screen Orientation API (iOS Safari, most
  // desktops with non-rotatable displays) — the fullscreen still works, we
  // just can't force rotation there.
  useEffect(() => {
    const onFsChange = () => {
      const el = containerRef.current;
      const inFs = document.fullscreenElement && document.fullscreenElement === el;
      const orientation = window.screen && window.screen.orientation;
      if (!orientation) return;
      if (inFs) {
        if (typeof orientation.lock === "function") {
          const p = orientation.lock("landscape");
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      } else if (typeof orientation.unlock === "function") {
        try { orientation.unlock(); } catch (_) { /* noop */ }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      // Ensure we release the lock if the component unmounts while in fullscreen.
      const orientation = window.screen && window.screen.orientation;
      if (orientation && typeof orientation.unlock === "function") {
        try { orientation.unlock(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  useEffect(() => () => clearGap(), []);

  if (!list.length) return null;

  return (
    <section data-testid="ad-videos" className="w-full bg-ivory py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={containerRef} className="relative w-full aspect-16-9 rounded-lg overflow-hidden bg-brown-dark shadow-lg">
          {list.map((src, i) => (
            <video
              key={`${src}-${i}`}
              ref={(el) => (videoRefs.current[i] = el)}
              src={src}
              muted={muted}
              autoPlay={i === 0}
              playsInline
              preload={i === active ? "metadata" : "none"}
              onTimeUpdate={i === active ? onTimeUpdate : undefined}
              onLoadedMetadata={(e) => { if (i === active) setDuration(e.target.duration || 0); }}
              onDurationChange={(e) => { if (i === active) setDuration(e.target.duration || 0); }}
              onEnded={i === active ? handleEnded : undefined}
              onPlay={i === active ? () => setPlaying(true) : undefined}
              onPause={i === active ? () => setPlaying(false) : undefined}
              onError={() => {}}
              onClick={i === active ? togglePlay : undefined}
              className={`absolute inset-0 w-full h-full object-cover cursor-pointer ${i === active ? "opacity-100" : "opacity-0 pointer-events-none"} transition-opacity duration-500`}
              data-testid={`ad-video-${i}`}
            />
          ))}

          {/* Mute button — top right */}
          <button
            data-testid="ad-mute-btn"
            onClick={toggleMute}
            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70 transition-colors inline-flex items-center justify-center"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          {/* Bottom controls */}
          <div className="absolute bottom-0 inset-x-0 video-controls-bg text-white px-3 md:px-5 pt-8 pb-3 z-10 select-none">
            {/* Progress bar — draggable / tappable */}
            <div
              ref={progressRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              data-testid="ad-progress"
              className="w-full h-3 flex items-center cursor-pointer touch-none group"
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
            >
              <div className="relative w-full h-1 bg-white/30 rounded-full group-hover:h-1.5 transition-all">
                <div className="h-full bg-brand-red rounded-full" style={{ width: `${progress}%` }} />
                <span
                  className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-brand-red border-2 border-white shadow-md"
                  style={{ left: `calc(${progress}% - 7px)` }}
                />
              </div>
            </div>

            <div className="mt-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <button
                  data-testid="ad-play-btn"
                  onClick={togglePlay}
                  className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center justify-center transition-colors"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 translate-x-[1px]" />}
                </button>
                <span className="text-[11px] font-medium tracking-wide tabular-nums">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>
              <button
                data-testid="ad-fullscreen-btn"
                onClick={toggleFullscreen}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center justify-center transition-colors"
                aria-label="Fullscreen"
              >
                <Maximize className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center gap-2">
          {list.map((_, i) => (
            <button
              key={i}
              data-testid={`ad-dot-${i}`}
              onClick={() => onDotClick(i)}
              aria-label={`Ad video ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === active ? "bg-brand-red w-8" : "bg-brown-border w-2"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
